import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription, filter } from 'rxjs';
import { EDeviceModel, isThOneGw } from 'src/app/conf/device-models';
import { Device } from 'src/app/model/device';
import { DeviceStatus } from 'src/app/model/device-status';
import { SignalData } from 'src/app/model/signal-level';

import { AppState } from 'src/app/store/app.reducer';
import * as DeviceActions from 'src/app/store/device/device.actions';
import * as DeviceThActions from 'src/app/store/device-th/device-th.actions';
import { selectCurrentDevice, selectCurrentDeviceSignalData, selectCurrentDeviceStatus, selectReadingSignal } from 'src/app/store/device/device.selector';
import { Thermo } from 'src/app/model/thermo';
import { TranslateService } from '@ngx-translate/core';
import { selectCurrentDeviceThermo } from 'src/app/store/device-th/device-th.selector';


@Component({
	selector: 'app-signal-level',
	templateUrl: './signal-level.component.html',
	providers: [],
})
export class SignalLevelComponent implements OnInit, OnDestroy {
	thermoDevicesSub: Subscription;
	thermo: Thermo;
	deviceStatus$: Observable<DeviceStatus | undefined>;
	deviceStatusSub: Subscription | undefined;
	currentDeviceSignalData$: Observable<SignalData | undefined>;
	currentDeviceSignalDataSub: Subscription | undefined;
	readingSignal$: Observable<boolean | undefined>;
	readingSignalSub: Subscription | undefined;


	device: Device;
	device$: Observable<Device | undefined>;
	deviceSub: Subscription | undefined;

	isTHGW: boolean;
	thSignalString: string;

	configured: boolean;
	online: boolean;
	readingSignal: boolean;
	signalData: SignalData = new SignalData();

	flagImagePath = './assets/images/device_icons/';
	flagImageFormat = '.png';

	constructor(
		private store: Store<AppState>,
		private translate: TranslateService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		// Store selectors
		this.deviceStatus$ = this.store.select(selectCurrentDeviceStatus);
		this.currentDeviceSignalData$ = this.store.select(selectCurrentDeviceSignalData);
		this.readingSignal$ = this.store.select(selectReadingSignal);
		this.device$ = this.store.select(selectCurrentDevice);
	}

	ngOnInit(): void {
		this.deviceStatusSub = this.deviceStatus$.subscribe(deviceState => {
			if (deviceState) {
				this.configured = deviceState.Configured === 0;
				this.online = deviceState.Online;
			}
		});
		this.currentDeviceSignalDataSub = this.currentDeviceSignalData$.pipe(filter(sd => !!sd))
			.subscribe(
				signalData => {
					this.signalData = signalData;
					if (this.isGtw4G() || this.isGsmGateway()) {
						const newSign = Math.trunc(signalData.SignalLevel / 31 * 100);
						this.signalData.SignalLevel = newSign;
					}
				}
			);
		this.readingSignalSub = this.readingSignal$.subscribe(
			readingSignal => {
				this.readingSignal = readingSignal;
			}
		);
		this.deviceSub = this.device$?.subscribe(
			(device) => {
				if (device !== undefined && device !== null) {
					this.device = device;
					if (isThOneGw(this.device.ModelId)) {
						this.isTHGW = true;
						this.store.dispatch(DeviceThActions.getThermoDevice({ keycode: device.Keycode }));
					}
				}
			}
		);
		this.thermoDevicesSub = this.store.select(selectCurrentDeviceThermo).subscribe(
			deviceThermo => {
				this.thermo = deviceThermo;
				if (this.thermo !== undefined && this.thermo.signal_level !== undefined) {
					this.thSignalString = this.thermo.signal_level;
				} else {
					this.thermo = null;
				}
			}
		);
	}

	ngOnDestroy(): void {
		this.deviceSub?.unsubscribe();
		this.deviceStatusSub?.unsubscribe();
		this.currentDeviceSignalDataSub?.unsubscribe();
		this.readingSignalSub?.unsubscribe();
		this.thermoDevicesSub?.unsubscribe();
	}

	isGtw4G(): boolean {
		return this.device !== undefined && (this.device.ModelId === EDeviceModel.GW4G_RADIO || this.device.ModelId === EDeviceModel.GW4G_STANDALONE);
	}


	isGsmGateway(): boolean {
		return this.device !== undefined && (this.device.ModelId === EDeviceModel.RGSM001 || this.device.ModelId === EDeviceModel.RGSM001S);
	}


	readSignal(): void {
		this.store.dispatch(DeviceActions.getSignalLevel({}));
	}

	canReadSignal(): boolean {
		return this.configured && this.online;
	}

	getSignalIcon(level: number): string {
		if (level > 75) {
			return 'signal_4';
		} else if (level > 50) {
			return 'signal_3';
		} else if (level > 25) {
			return 'signal_2';
		} else if (level > 0) {
			return 'signal_1';
		} else {
			return 'signal_off';
		}
	}

	getSignalIconThGw(): string {
		if (this.thSignalString === 'Eccellente') {
			return 'signal_4';
		} else if (this.thSignalString === 'Buono') {
			return 'signal_3';
		} else if (this.thSignalString === 'Discreto') {
			return 'signal_2';
		} else if (this.thSignalString === 'Scarso') {
			return 'signal_1';
		} else {
			return 'signal_off';
		}
	}

	getSignalTxtThGw(): string {
		if (this.thSignalString === 'Eccellente') {
			return this.translate.instant('TH_SIGNAL_EXCELLENT');
		} else if (this.thSignalString === 'Buono') {
			return this.translate.instant('TH_SIGNAL_GOOD');
		} else if (this.thSignalString === 'Discreto') {
			return this.translate.instant('TH_SIGNAL_DECENT');
		} else if (this.thSignalString === 'Scarso') {
			return this.translate.instant('TH_SIGNAL_LOW');
		} else {
			return this.translate.instant('TH_SIGNAL_OFF');
		}
	}

}
